/**
 * Automation Settings — tenant's own runtime pause switch for automation.
 * Independent of subscription plan entitlement; a super-admin can also
 * toggle the same switch on the tenant's behalf from the saas console.
 */
import { useAutomationSettings } from '@/hooks/useAutomationSettings';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import React from 'react';

const AutomationSettings: React.FC = () => {
  const { paused, loading, saving, error, success, updatePaused } = useAutomationSettings();

  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Automation
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pausing automation stops scheduled campaigns, behavior-triggered campaigns
          (no activity / no reply), and automation workflows from running — both new
          runs and ones already in progress. Nothing is deleted or deactivated, and
          you can still edit campaigns and workflows while paused. Sending a campaign
          manually is not affected.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <FormControlLabel
            control={
              <Switch
                checked={paused}
                disabled={saving}
                onChange={(e) => updatePaused(e.target.checked)}
              />
            }
            label={paused ? 'Automation is paused' : 'Automation is active'}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default AutomationSettings;
